import React, { useState, useEffect } from "react";
import { Alert, App, Button, Collapse, Divider, Grid, Space, Switch } from "antd";
import { UserOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { useAntdTheme } from "../../shared/hooks";

import ItemInfo from "../../shared/components/items/ItemInfo";
import MuiDateTimePicker from "../../shared/components/pickers/MuiDateTimePicker";

import { postFetch, sortItems, API_URL, COLORS } from "../../shared/utils/Util";
import { formatAddress } from "../../shared/utils/TypedUtil";
import PrintPointCardButton from "../../shared/components/buttons/PrintPointCardButton";
import PrintNonLabelItemButton from "../../shared/components/buttons/PrintNonLabelItemButton";
import PrintLabelItemButton from "../../shared/components/buttons/PrintLabelItemButton";
import { Order } from "../../types/model";
import {
  isLabelAutoPrintDisabled,
  setLabelAutoPrintDisabled,
  setManuallyPrinted,
  wasManuallyPrinted,
} from "../../services/labelAutoPrintDb";

const { Panel } = Collapse;

interface OrderDetailsProps {
  order: Order & {
    display_after?: string;
    point_credit?: {
      id: string;
      claim_customer_id?: string;
    };
  };
  pending?: boolean;
  restaurantId: string;
}

const OrderDetails: React.FC<OrderDetailsProps> = ({ order, pending, restaurantId }) => {
  const { notification } = App.useApp();
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const screens = Grid.useBreakpoint();
  const initSelectedDate = order.display_after ? dayjs(order.display_after) : null;
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(initSelectedDate);
  const [labelAutoPrintDisabled, setLabelAutoPrintDisabledState] = useState<boolean>(false);
  const [toggleLocked, setToggleLocked] = useState<boolean>(false);

  const items = sortItems(order.items);

  // Responsive button sizing - includes landscape phones
  const isMobile = !screens.lg; // Catches phones in both portrait and landscape
  const buttonSize = isMobile ? "middle" : "large";
  const buttonHeight = isMobile ? token.controlHeight : token.controlHeightLG;
  const buttonFontSize = isMobile ? "14px" : "15px";
  const buttonMinWidth = isMobile ? "120px" : "150px";

  // Load label auto-print preference from IndexedDB
  useEffect(() => {
    if (pending && order.id) {
      isLabelAutoPrintDisabled(order.id)
        .then(setLabelAutoPrintDisabledState)
        .catch(() => {});
      wasManuallyPrinted(order.id)
        .then(setToggleLocked)
        .catch(() => {});
    }
  }, [order.id, pending]);

  // Handle toggle change
  const handleLabelAutoPrintToggle = async (checked: boolean) => {
    setLabelAutoPrintDisabledState(!checked);
    try {
      await setLabelAutoPrintDisabled(order.id, !checked);
    } catch (err) {
      console.error("Failed to save label auto-print preference", err);
    }
  };

  // Manual print buttons should be disabled when auto-print is ON (for pending orders)
  // This prevents double printing - user must explicitly opt into manual printing
  const manualPrintDisabled = pending && !labelAutoPrintDisabled;

  // Disable auto-print and lock toggle when user manually prints beverage labels
  const handleManualBeveragePrint = async () => {
    // Lock the toggle so user can't turn auto-print back ON after manual printing
    setToggleLocked(true);
    try {
      await setManuallyPrinted(order.id);
    } catch (err) {
      console.error("Failed to save manually printed state", err);
    }
  };

  return (
    <div style={{ fontSize: "15px", lineHeight: 1.6 }}>
      {(!items || items.length === 0 || !order.pos_order_name) && (
        <Alert
          message={t("order_incomplete_warning")}
          type="warning"
          showIcon
          style={{
            marginBottom: token.marginLG,
            borderRadius: token.borderRadius,
          }}
        />
      )}
      {pending && (
        <div style={{ marginBottom: token.marginLG }}>
          <MuiDateTimePicker
            format="ddd, MMM DD, HH:mm"
            value={selectedDate}
            placeholder={t("select_reminder_time")}
            disabledDate={(current) => current && current.isBefore(dayjs(), "day")}
            onChange={(m) => {
              setSelectedDate(m);
            }}
            style={{
              width: "100%",
              marginBottom: token.marginMD,
            }}
          />
          {selectedDate && (
            <Space size={isMobile ? "small" : "middle"} wrap>
              <Button
                type="primary"
                size={buttonSize}
                style={{
                  height: buttonHeight,
                  fontSize: buttonFontSize,
                }}
                onClick={() => {
                  postFetch(
                    `${API_URL}/v1/r/${restaurantId}/orders/${order.id}/display_after`,
                    { display_after_time: selectedDate.format() },
                    () => {
                      notification.success({
                        message: t("reminder_set", {
                          time: selectedDate.format("YYYY-MM-DD HH:mm"),
                        }),
                      });
                    },
                    () => {
                      notification.error({
                        message: t("error_occurred"),
                        description: t("please_try_again_later"),
                      });
                    }
                  );
                }}
              >
                {t("remind_me")}
              </Button>
              <Button
                size={buttonSize}
                style={{
                  height: buttonHeight,
                  fontSize: buttonFontSize,
                }}
                onClick={() => {
                  postFetch(
                    `${API_URL}/v1/r/${restaurantId}/orders/${order.id}/display_after/reset`,
                    {},
                    () => {
                      notification.success({
                        message: t("reminder_cancelled"),
                      });
                      setSelectedDate(null);
                    },
                    () => {
                      notification.error({
                        message: t("error_occurred"),
                        description: t("please_try_again_later"),
                      });
                    }
                  );
                }}
              >
                {t("cancel_reminder")}
              </Button>
            </Space>
          )}
        </div>
      )}
      {order.notes && (
        <div
          style={{
            padding: token.paddingMD,
            background: token.colorBgLayout,
            borderRadius: token.borderRadius,
            marginBottom: token.marginLG,
            borderLeft: `4px solid ${token.colorPrimary}`,
          }}
        >
          <strong>{t("notes")}：</strong>
          {order.notes}
        </div>
      )}

      {pending && (
        <div
          onClick={() => !toggleLocked && handleLabelAutoPrintToggle(labelAutoPrintDisabled)}
          style={{
            marginBottom: token.marginLG,
            padding: token.paddingMD,
            background: token.colorBgContainer,
            borderRadius: token.borderRadius,
            border: `1px solid ${token.colorBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: toggleLocked ? "not-allowed" : "pointer",
            userSelect: "none",
            opacity: toggleLocked ? 0.6 : 1,
          }}
        >
          <span style={{ fontWeight: token.fontWeightStrong }}>{t("auto_print_on_start")}</span>
          <Switch
            checked={!labelAutoPrintDisabled}
            disabled={toggleLocked}
            onClick={(_checked, e) => e.stopPropagation()}
            onChange={handleLabelAutoPrintToggle}
            checkedChildren={t("on")}
            unCheckedChildren={t("off")}
          />
        </div>
      )}

      <div
        style={{
          marginBottom: token.marginLG,
          display: "flex",
          gap: isMobile ? token.marginSM : token.marginMD,
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: 1, minWidth: buttonMinWidth }}>
          <PrintLabelItemButton
            key="print_label_items"
            size={buttonSize}
            disabled={manualPrintDisabled}
            style={{
              height: buttonHeight,
              fontSize: buttonFontSize,
              fontWeight: token.fontWeightStrong,
              borderRadius: token.borderRadius,
              width: "100%",
            }}
            handleError={(err) => {
              notification.error({
                message: t("unable_to_print"),
                description: t("please_try_again_later"),
              });
            }}
            onPrint={handleManualBeveragePrint}
            order={order}
          >
            {t("print_beverage_labels")}
          </PrintLabelItemButton>
        </div>
        <div style={{ flex: 1, minWidth: buttonMinWidth }}>
          <PrintNonLabelItemButton
            key="print_all_item_in_order"
            size={buttonSize}
            style={{
              height: buttonHeight,
              fontSize: buttonFontSize,
              fontWeight: token.fontWeightStrong,
              borderRadius: token.borderRadius,
              width: "100%",
            }}
            handleError={(err) => {
              notification.error({
                message: t("unable_to_print"),
                description: t("please_try_again_later"),
              });
            }}
            order={order}
          >
            {t("print_non_beverage_labels")}
          </PrintNonLabelItemButton>
        </div>
        {order.point_credit && !order.point_credit.claim_customer_id && (
          <div style={{ flex: 1, minWidth: buttonMinWidth }}>
            <PrintPointCardButton
              key="print_point_card"
              size={buttonSize}
              style={{
                height: buttonHeight,
                fontSize: buttonFontSize,
                fontWeight: token.fontWeightStrong,
                borderRadius: token.borderRadius,
                width: "100%",
              }}
              handleError={(err) => {
                notification.error({
                  message: t("unable_to_print"),
                  description: t("please_try_again_later"),
                });
              }}
              order={order}
            >
              {t("print_points")}
            </PrintPointCardButton>
          </div>
        )}
      </div>

      {order.point_credit && order.point_credit.claim_customer_id && (
        <Alert
          message={t("points_claimed")}
          type="success"
          showIcon
          style={{
            marginBottom: token.marginLG,
            borderRadius: token.borderRadius,
          }}
        />
      )}

      {order.customer != null && (
        <Collapse
          defaultActiveKey={[]}
          style={{
            marginBottom: token.marginLG,
            borderRadius: token.borderRadius,
            border: `1px solid ${token.colorBorder}`,
          }}
        >
          <Panel
            header={
              <span
                style={{
                  fontSize: "15px",
                  fontWeight: token.fontWeightStrong,
                }}
              >
                <UserOutlined style={{ marginRight: token.marginSM }} /> Customer
              </span>
            }
            key="customer-info"
          >
            <div
              style={{
                lineHeight: 1.8,
                color: token.colorTextSecondary,
              }}
            >
              <p style={{ margin: `${token.marginXS}px 0` }}>
                <strong>Name:</strong> {order.customer.name}
              </p>
              <p style={{ margin: `${token.marginXS}px 0` }}>
                <strong>Phone:</strong> {order.customer.phone}
              </p>
              <p style={{ margin: `${token.marginXS}px 0` }}>
                <strong>Email:</strong> {order.customer.email}
              </p>
              {order.customer.address && (
                <p style={{ margin: `${token.marginXS}px 0` }}>
                  <strong>Address:</strong> {formatAddress(order.customer.address)}
                </p>
              )}
            </div>
          </Panel>
        </Collapse>
      )}
      {items && items.length > 0 && (
        <div
          style={{
            marginBottom: token.marginLG,
            border: `1px solid ${token.colorBorder}`,
            borderRadius: token.borderRadius,
            overflow: "hidden",
          }}
        >
          {items.map((item, index) => {
            let qtyColor = COLORS[item.qty % COLORS.length];
            const isLastItem = index === items.length - 1;
            if (!item.actions) {
              qtyColor = "rgba(0,0,0,.25)";
            }
            return (
              <div
                key={item.id}
                style={{
                  padding: token.paddingMD,
                  borderBottom: isLastItem ? "none" : `1px solid ${token.colorBorder}`,
                  background: index % 2 === 0 ? token.colorBgContainer : token.colorBgLayout,
                }}
              >
                <ItemInfo item={item.attributes} qty={item.qty} qtyColor={qtyColor} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default OrderDetails;
